import { get, type Me } from '../api.js';
import { button, chip, clear, el, empty, fmtMoney, mono, panel, row, skeleton } from '../ui.js';

/**
 * What to do right now, and nothing else.
 *
 * WHY THIS SCREEN EXISTS
 * Signing in used to land on the list of every lead in the agency. That is a
 * filing cabinet: correct, complete, and no help at all at nine in the morning.
 * Everything the platform knows about what today should look like was spread
 * across four screens, and the one an agent opens first was the one that said
 * least.
 *
 * WHAT IT IS ALLOWED TO SHOW
 * Only things somebody can act on in the next hour, in the order they cost most
 * to ignore:
 *
 *   1. Callbacks that have come due. Somebody asked to be rung by now.
 *   2. Money already earned that is sitting on the wrong draft day.
 *   3. Windows about to close, because those calls stop being possible.
 *   4. People in the book with a specific dated reason to ring them.
 *   5. Your own numbers, and the one thing worth fixing.
 *
 * Nothing on it is new. Every panel reads a route that already existed, which
 * is the point — the work was already being done and nobody could see it in one
 * place at the moment it mattered.
 *
 * WHAT IT REFUSES TO BE
 * A dashboard. There are no charts, no totals for their own sake, and nothing
 * that exists to look impressive. A number nobody can act on today is a number
 * that belongs on the Production tab, and putting it here would train people to
 * skim past the four things that are actually urgent.
 */

interface Queue {
  callable: Array<{ id: string; firstName: string | null; lastName: string | null; state: string | null }>;
  counts: { callable: number; waiting: number; blocked: number; scanned: number };
  nextOpensInMinutes: number | null;
  closingSoon: number;
  dueCallbacks: number;
  promised: number;
  resting: number;
  blockedBy: Array<{ code: string; check: string; message: string; leads: number }>;
}

interface AtRisk {
  windowMonths: number;
  failing: { cases: number; annualPremiumCents: number; commissionCents: number | null };
  tight: { cases: number; annualPremiumCents: number; commissionCents: number | null };
  withoutRate: number;
  worst: Array<{
    policyId: string; leadId: string; insuredName: string | null;
    commissionCents: number | null; draftDay: number | null; safeDays: number[];
  }>;
}

interface Reason {
  kind: string; leadId: string; headline: string; detail: string;
  servicing: boolean; daysUntil: number | null;
}

interface Warm {
  items: Array<{
    leadId: string; name: string; phone: string | null; state: string | null;
    status: string; statusLabel: string; ownerName: string | null;
    daysSince: number; goingCold: boolean;
  }>;
  cold: number;
  coldAfterDays: number;
}

interface ScoreRow {
  userId: string; name: string;
  dials: number; contacts: number; contactRate: number | null;
  casesOpened: number; placed: number; annualPremiumCents: number;
  commissionExpectedCents: number | null; commissionPaidCents: number | null;
  withoutRate: number;
  finding: 'too_early' | 'fine' | 'dials' | 'reach' | 'close';
  note: string | null;
}
interface Board {
  windowDays: number; thin: boolean; rows: ScoreRow[];
  floor: { dials: number; contacts: number; contactRate: number | null };
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

/**
 * Turns a failed fetch into a missing panel rather than a dead page.
 *
 * WHY IT TAKES A PROMISE AND NOT A PATH
 * The obvious shape is `tryGet('/v1/whatever')`. It reads better and it hides
 * every URL on this screen from the scanner that checks each API route is
 * reachable from something a person can click — `\bget(` does not match
 * `tryGet(`. Five routes on the busiest new screen in the product were
 * invisible to it, and the test meant to police what this screen may read was
 * passing without matching anything at all.
 *
 * Wrapping the promise keeps `get<T>('/v1/…')` written out at the call site,
 * where both the scanner and a person can see it.
 */
async function soft<T>(p: Promise<T>): Promise<T | null> {
  try { return await p; } catch { return null; }
}

export function todayView(me: Me, go: (p: string) => void): HTMLElement {
  const host = el('div');
  const first = me.user.firstName || 'there';

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Today' }),
    el('h1', { text: `Morning, ${first}` })));

  const body = el('div');
  host.append(body);
  clear(body).append(skeleton(4));

  async function load(): Promise<void> {
    /*
     * All at once, and each allowed to fail on its own. Five sequential
     * requests is a second and a half of an empty screen on the one page
     * somebody opens every morning; and a screen where one broken panel takes
     * the other four with it is worse than any of them being missing.
     */
    const [queue, risk, reasons, board, warm] = await Promise.all([
      soft(get<Queue>('/v1/leads/queue?limit=20')),
      soft(get<AtRisk>('/v1/commission-at-risk')),
      soft(get<Reason[]>('/v1/call-reasons?limit=6')),
      soft(get<Board>('/v1/scoreboard')),
      soft(get<Warm>('/v1/warm')),
    ]);
    clear(body);

    const parts: HTMLElement[] = [];
    const dial = (label: string): HTMLElement => button(label, 'pri', () => go('/dialing'));

    /* 1. Somebody asked to be rung by now. Nothing outranks that. */
    if (queue && queue.dueCallbacks > 0) {
      parts.push(panel(null, el('div', { class: 'okbox' },
        el('b', null, `${queue.dueCallbacks} callback${queue.dueCallbacks === 1 ? '' : 's'} due now. `),
        'These people asked you to ring them by this time. They are at the very top of your '
        + 'queue, and they are worth more than anything else in front of you.',
        el('div', { style: 'margin-top:12px' }, dial('Start calling')))));
    }

    /* 2. Money already earned, sitting on the wrong day. */
    if (risk) {
      const exposed = risk.failing.cases + risk.tight.cases;
      const money = risk.failing.commissionCents === null && risk.tight.commissionCents === null
        ? null
        : (risk.failing.commissionCents ?? 0) + (risk.tight.commissionCents ?? 0);
      if (exposed > 0) {
        const worst = risk.worst.slice(0, 3);
        parts.push(panel('Your money on a day the client has not been paid',
          el('div', { class: 'warnbox' },
            el('b', null, money === null
              ? `${exposed} case${exposed === 1 ? '' : 's'} are drafting before the money lands. `
              : `${fmtMoney(money)} across ${exposed} case${exposed === 1 ? '' : 's'}. `),
            'Moving the draft date is a phone call to the carrier. Leaving it is a bounced '
            + 'draft, a dead policy and a chargeback.'),
          worst.length
            ? el('div', { class: 'rows', style: 'margin-top:14px' }, ...worst.map((w) => row({
              title: w.insuredName ?? 'Unnamed case',
              sub: w.draftDay === null
                ? 'no draft day recorded'
                : `drafts on the ${ordinal(w.draftDay)}`
                  + (w.safeDays.length ? ` — move to the ${w.safeDays.slice(0, 2).map(ordinal).join(' or ')}` : ''),
              right: el('div', { style: 'display:flex;gap:8px;align-items:center' },
                w.commissionCents === null ? null : mono(fmtMoney(w.commissionCents)),
                button('Open', 'sm', () => go(`/leads/${w.leadId}`))),
            })))
            : null,
          exposed > worst.length
            ? el('p', {
              style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0 0',
              text: `${exposed - worst.length} more on the Cases screen.`,
            })
            : null));
      }
    }

    /*
     * 3. People who already said yes.
     *
     * Second only to a due callback, and for the same reason: the work of
     * finding them and the work of persuading them is already done and paid
     * for. An agent marks somebody Interested, the lead leaves the cold queue,
     * and until this list existed it went nowhere at all — twelve of those in a
     * week is twelve people who agreed to hear more and were never rung again.
     */
    if (warm && warm.items.length) {
      const shown = warm.items.slice(0, 6);
      parts.push(panel(`${warm.items.length} said yes and have no application open`,
        warm.cold
          ? el('div', { class: 'warnbox', style: 'margin-bottom:12px' },
            el('b', null, `${warm.cold} of them have been waiting more than ${warm.coldAfterDays} days. `),
            'Interest does not keep. These were sold to once already, and every day nobody goes '
            + 'back is a day closer to starting over with somebody else.')
          : el('div', { class: 'note', style: 'margin-bottom:12px' },
            el('b', null, 'The warmest names you have. '),
            'Each of these was spoken to and agreed to go further, and nothing has been written '
            + 'for them yet.'),
        el('div', { class: 'rows' }, ...shown.map((w) => row({
          title: w.name,
          sub: `${w.statusLabel} · ${w.daysSince === 0 ? 'today' : `${w.daysSince} day${w.daysSince === 1 ? '' : 's'} ago`}`
            + (w.ownerName ? ` · ${w.ownerName}` : ''),
          right: el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' },
            w.goingCold ? chip('going cold', 'crit') : null,
            button('Open', 'sm', () => go(`/leads/${w.leadId}`))),
        }))),
        warm.items.length > shown.length
          ? el('p', {
            style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0 0',
            text: `${warm.items.length - shown.length} more in the book, under Leads.`,
          })
          : null));
    }

    /* 4. Calls that stop being possible if nobody makes them. */
    if (queue && queue.closingSoon > 0) {
      parts.push(panel(null, el('div', { class: 'warnbox' },
        el('b', null, `${queue.closingSoon} people stop being callable within the hour. `),
        'Their state closes before the rest of your book does. Calls not made now are calls '
        + 'that cannot be made today.',
        el('div', { style: 'margin-top:12px' }, dial('Work those first')))));
    }

    /* 4. People who already bought, with a dated reason. */
    if (reasons && reasons.length) {
      parts.push(panel(`${reasons.length} people worth a call today`,
        el('div', { class: 'note', style: 'margin-bottom:12px' },
          el('b', null, 'From your own book, not from a list. '),
          'Each one is a specific piece of money at a specific date. A call about a policy '
          + 'somebody already owns is a service call and should sound like one.'),
        el('div', { class: 'rows' }, ...reasons.map((r) => row({
          title: r.headline,
          sub: r.detail,
          right: el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' },
            r.servicing ? chip('they already bought', 'cop') : null,
            r.daysUntil === null ? null : chip(
              r.daysUntil <= 0 ? 'today' : r.daysUntil === 1 ? 'tomorrow' : `in ${r.daysUntil} days`,
              r.daysUntil <= 2 ? 'crit' : '',
            ),
            button('Open', 'sm', () => go(`/leads/${r.leadId}`))),
        })))));
    }

    /*
     * 5. The queue itself — and, when it is empty, what to do about it.
     *
     * THE TWO DEAD ENDS THIS SCREEN USED TO BE FOR A NEW AGENCY
     * Today became the front door, and a brand new agency landed on "nothing
     * needs you this minute" with no way forward. Worse was the state every
     * agency is in on day two: leads imported, nothing scrubbed, every single
     * one blocked, and a screen that said "blocked — time will not fix these"
     * without saying what would. Somebody in that position goes hunting through
     * three settings screens and concludes the software is broken.
     */
    if (queue && queue.counts.scanned === 0) {
      parts.push(panel('Nothing in the book yet',
        el('div', { class: 'note' },
          el('b', null, 'This agency has no leads. '),
          'Bring a list in and they appear here as their calling windows open. Everything else '
          + 'on this screen is about people, so until then there is nothing to show you.'),
        el('div', { style: 'margin-top:12px;display:flex;gap:8px;flex-wrap:wrap' },
          button('Import a list', 'pri', () => go('/import')),
          button('Add one by hand', 'sm', () => go('/leads')))));
    } else if (queue && queue.counts.callable === 0 && queue.counts.blocked > 0) {
      /*
       * The gate already knows the answer for every one of those rows, and it
       * is almost always the same two or three things on a fresh book. Naming
       * them with counts turns "the software is broken" into an afternoon of
       * work somebody can actually do.
       */
      parts.push(panel(`Nothing callable — ${queue.counts.blocked} leads are blocked`,
        el('div', { class: 'warnbox' },
          el('b', null, 'Waiting will not fix these. '),
          'Each of these is a check that has not been satisfied yet, not a lead that has gone '
          + 'bad. A freshly imported book is usually stopped by two or three of them at once.'),
        queue.blockedBy.length
          ? el('div', { class: 'rows', style: 'margin-top:14px' }, ...queue.blockedBy.map((b) => row({
            title: `${b.leads.toLocaleString()} — ${b.check}`,
            sub: b.message,
            right: b.code === 'scrub_stale'
              ? button('Open compliance', 'sm', () => go('/compliance'))
              : b.code === 'consent_missing' || b.code === 'consent_channel'
                ? button('Calling rules', 'sm', () => go('/settings/compliance'))
                : b.code === 'dialing_paused'
                  ? button('Dialling', 'sm', () => go('/dialing'))
                  : button('Open the book', 'sm', () => go('/leads')),
          })))
          : null));
    }

    if (queue) {
      const { counts } = queue;
      parts.push(panel('Your queue',
        el('div', { class: 'tiles' },
          tileOf(String(counts.callable), 'Callable now',
            counts.callable ? 'every check passed' : 'nothing this minute', 'var(--good)'),
          tileOf(String(counts.waiting), 'Waiting on the clock',
            queue.nextOpensInMinutes === null
              ? 'none waiting'
              : `next opens in ${queue.nextOpensInMinutes} min`,
            'var(--copper)'),
          tileOf(String(counts.blocked), 'Blocked',
            counts.blocked ? 'time will not fix these' : 'nothing blocked', 'var(--crit)')),
        el('div', { style: 'margin-top:14px;display:flex;gap:8px;flex-wrap:wrap' },
          counts.callable > 0 ? dial('Start calling') : null,
          button('Open the book', 'sm', () => go('/leads')),
          counts.blocked > 0
            ? button('See what is blocked', 'sm', () => go('/leads'))
            : null)));
    }

    /* 6. Your own numbers, and the one thing worth fixing. */
    if (board) {
      const mine = board.rows.find((r) => r.userId === me.user.id) ?? board.rows[0];
      if (mine) {
        const owed = mine.commissionExpectedCents;
        parts.push(panel(`Your last ${board.windowDays} days`,
          el('div', { class: 'tiles' },
            tileOf(mine.dials.toLocaleString(), 'Dials', 'calls you recorded', 'var(--iris)'),
            tileOf(mine.contacts.toLocaleString(), 'People you spoke to',
              mine.contactRate === null
                ? 'not enough dials to rate yet'
                : `${Math.round(mine.contactRate * 100)}% of dials`,
              'var(--good)'),
            tileOf(String(mine.placed), 'Placed', fmtMoney(mine.annualPremiumCents), 'var(--copper)'),
            // The number an agent actually wants, and the reason it is null
            // rather than nought: nobody recorded a rate is not "you are owed
            // nothing", and only one of those gets fixed.
            tileOf(owed === null ? '—' : fmtMoney(owed), 'Commission outstanding',
              owed === null ? 'no rate recorded on your cases' : 'placed, not yet marked paid',
              'var(--iris)')),
          mine.withoutRate
            ? el('p', {
              style: 'font-size:12.5px;color:var(--ink-3);margin:12px 0 0',
              text: `${mine.withoutRate} of your placed cases have no commission rate on them, so `
                + 'that figure is lower than the truth.',
            })
            : null,
          mine.note && mine.finding !== 'fine' && mine.finding !== 'too_early'
            ? el('div', { class: 'note', style: 'margin-top:14px' },
              el('b', null, 'Worth knowing: '), mine.note)
            : null));
      }
    }

    if (!parts.length) {
      body.append(panel(null, empty('Nothing needs you this minute',
        'No callbacks due, nothing drafting early, and no window about to close. When any of '
        + 'that changes it appears here first.')));
      return;
    }
    for (const part of parts) body.append(part);
  }

  void load();
  return host;
}

/** A tile, without reaching into another view for one. */
const tileOf = (
  value: string, label: string, note: string | null, accent: string,
): HTMLElement => el('div', { class: 'tile', style: `--accent:${accent}` },
  el('div', { class: 'k', text: label }),
  el('div', { class: 'v', text: value }),
  note ? el('div', { class: 'd', text: note }) : null);
